<!-- src/components/comparisons/WeeklyActualRevenueContent.vue -->

<template>
  <div class="core-page-content">
    <div v-if="loading" class="core-feedback core-feedback--neutral">
      Loading weekly revenue window…
    </div>

    <div v-else-if="error" class="core-feedback core-feedback--error">
      {{ error }}
    </div>

    <template v-else>
      <div class="chart-wrap">
        <div class="chart-scroll">
          <svg
            :width="svgW"
            :height="svgH"
            viewBox="0 0 900 320"
            role="img"
            aria-label="Rolling weekly revenue line chart showing actual, forecast, budget and actual revenue trend"
            class="chart-svg"
          >
            <rect
              :x="padL"
              :y="padT"
              :width="plotW"
              :height="plotH"
              class="plot-border"
            />

            <g v-for="(tick, index) in yTicks" :key="`yt-${index}`">
              <line
                :x1="padL"
                :x2="padL + plotW"
                :y1="tick.y"
                :y2="tick.y"
                class="grid-line"
              />

              <text
                :x="padL - 8"
                :y="tick.y + 4"
                text-anchor="end"
                font-size="11"
                class="axis-text"
              >
                {{ fmtMoney(tick.v) }}
              </text>
            </g>

            <g v-for="(week, index) in weeks" :key="week.key">
              <line
                :x1="xFor(index)"
                :x2="xFor(index)"
                :y1="padT + plotH"
                :y2="padT + plotH + 6"
                class="tick-line"
              />

              <text
                :x="xFor(index)"
                :y="padT + plotH + 22"
                text-anchor="middle"
                font-size="11"
                class="axis-text"
              >
                {{ week.labelShort }}
              </text>
            </g>

            <polyline
              v-if="pointsForecast"
              :points="pointsForecast"
              class="line-forecast"
            />

            <polyline v-if="pointsBudget" :points="pointsBudget" class="line-budget" />

            <line
              v-if="actualTrendLine"
              :x1="actualTrendLine.x1"
              :y1="actualTrendLine.y1"
              :x2="actualTrendLine.x2"
              :y2="actualTrendLine.y2"
              class="line-trend"
            />

            <polyline v-if="pointsActual" :points="pointsActual" class="line-actual" />

            <g v-for="(week, index) in weeks" :key="`pt-${week.key}`">
              <circle
                v-if="week.actual != null"
                :cx="xFor(index)"
                :cy="yFor(week.actual)"
                r="4"
                class="pt-actual"
              />

              <circle
                v-if="week.forecast != null"
                :cx="xFor(index)"
                :cy="yFor(week.forecast)"
                r="3.5"
                class="pt-forecast"
              />

              <circle
                v-if="week.budget != null"
                :cx="xFor(index)"
                :cy="yFor(week.budget)"
                r="3.5"
                class="pt-budget"
              />
            </g>
          </svg>
        </div>

        <div class="legend">
          <span class="legend-item">
            <span class="legend-swatch swatch-actual"></span>
            Actual
          </span>

          <span class="legend-item">
            <span class="legend-swatch swatch-forecast"></span>
            Forecast
          </span>

          <span class="legend-item">
            <span class="legend-swatch swatch-budget"></span>
            Budget
          </span>

          <span class="legend-item">
            <span class="legend-swatch swatch-trend"></span>
            Actual trend
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import { collection, doc, getDoc, getDocs } from "firebase/firestore";

import { db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { fetchForecastWeekDailyTotals } from "@/scripts/useScripts/useForecasts";

import * as formatter from "@/scripts/sharedHelpers/formatter";

import * as auditColours from "@/scripts/sharedHelpers/auditColours";

function fallbackCurrency(value, currency = "GBP") {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
  }).format(number);
}

function callFirstFunction(functions, args = [], fallback = null) {
  for (const fn of functions) {
    if (typeof fn !== "function") {
      continue;
    }

    try {
      return fn(...args);
    } catch (_) {
      // Try the next formatter.
    }
  }

  return typeof fallback === "function" ? fallback(...args) : fallback;
}

function fmtMoney(value) {
  return callFirstFunction(
    [
      formatter.formatCurrency,
      formatter.formatMoney,
      formatter.money,
      formatter.currency,
      formatter.toCurrency,
    ],
    [value],
    (fallbackValue) => fallbackCurrency(fallbackValue)
  );
}

function fmtShortDate(iso) {
  return callFirstFunction(
    [
      formatter.formatDateShort,
      formatter.formatShortDate,
      formatter.shortDate,
      formatter.displayDateShort,
    ],
    [iso],
    null
  );
}

function toNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}

function parseISODate(iso) {
  const [year, month, day] = String(iso || "")
    .split("-")
    .map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function toISODateLocal(date) {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function startOfDay(date) {
  const value = new Date(date);

  value.setHours(0, 0, 0, 0);

  return value;
}

function addDays(date, numberOfDays) {
  const value = new Date(date);

  value.setDate(value.getDate() + numberOfDays);

  return value;
}

function getWeekEndingFromAnyDate(iso) {
  const date = parseISODate(iso);

  if (!date) {
    return null;
  }

  const value = startOfDay(date);

  const day = value.getDay();

  const daysUntilSunday = (7 - day) % 7;

  return toISODateLocal(addDays(value, daysUntilSunday));
}

function formatWeekLabelShort(weekEndingIso) {
  const shared = fmtShortDate(weekEndingIso);

  if (typeof shared === "string" && shared.trim()) {
    return `W/E ${shared}`;
  }

  const date = parseISODate(weekEndingIso);

  if (!date) {
    return "—";
  }

  const day = String(date.getDate()).padStart(2, "0");

  const month = date.toLocaleString("en-GB", {
    month: "short",
  });

  return `W/E ${day} ${month}`;
}

function buildRollingWeekEndings(currentWeekEndingIso) {
  const current = parseISODate(currentWeekEndingIso);

  if (!current) {
    return [];
  }

  const currentDate = startOfDay(current);

  const output = [];

  for (let index = -12; index <= -1; index += 1) {
    output.push(toISODateLocal(addDays(currentDate, index * 7)));
  }

  return output;
}

function weekStartFromWeekEnding(weekEndingIso) {
  const end = parseISODate(weekEndingIso);

  if (!end) {
    return null;
  }

  return startOfDay(addDays(end, -6));
}

async function mapLimit(items, limit, fn) {
  const results = new Array(items.length);

  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;

      nextIndex += 1;

      results[currentIndex] = await fn(items[currentIndex], currentIndex);
    }
  }

  const workers = Array.from(
    {
      length: Math.max(1, limit),
    },
    () => worker()
  );

  await Promise.all(workers);

  return results;
}

const props = defineProps({
  forecastByWeekEnding: {
    type: Object,
    default: () => ({}),
  },

  budgetByWeekEnding: {
    type: Object,
    default: () => ({}),
  },

  resolveForecast: {
    type: Function,
    default: null,
  },

  resolveBudget: {
    type: Function,
    default: null,
  },

  edgeConcurrency: {
    type: Number,
    default: 12,
  },
});

const { venueId } = useVenueContext();

const loading = ref(false);

const error = ref("");

const weeks = ref([]);

async function fetchForecastForWeek(venueIdValue, weekEndingIso) {
  if (!venueIdValue || !weekEndingIso) {
    return null;
  }

  try {
    const dailyTotals = await fetchForecastWeekDailyTotals({
      venue_id: venueIdValue,

      week_ending_date: weekEndingIso,
    });

    if (!Array.isArray(dailyTotals)) {
      return null;
    }

    return dailyTotals.reduce((sum, value) => sum + toNumber(value), 0);
  } catch (fetchError) {
    console.warn(
      "[WeeklyRevenueRollingLinesOnlyContent] forecast read failed:",
      weekEndingIso,
      fetchError
    );

    return null;
  }
}

function getForecastForWeekSync(weekEndingIso) {
  if (typeof props.resolveForecast === "function") {
    const value = props.resolveForecast(weekEndingIso);

    return value == null ? null : Number(value);
  }

  const value = props.forecastByWeekEnding?.[weekEndingIso];

  return value == null ? null : Number(value);
}

async function getForecastForWeek(venueIdValue, weekEndingIso) {
  const suppliedForecast = getForecastForWeekSync(weekEndingIso);

  if (suppliedForecast != null && Number.isFinite(suppliedForecast)) {
    return suppliedForecast;
  }

  return fetchForecastForWeek(venueIdValue, weekEndingIso);
}

function extractBudgetLineAmount(data) {
  if (!data || typeof data !== "object") {
    return 0;
  }

  return toNumber(
    data.amount ??
      data.value ??
      data.total ??
      data.budget_amount ??
      data.budgetAmount ??
      0
  );
}

async function fetchBudgetForWeekFS(venueIdValue, weekEndingIso) {
  if (!venueIdValue || !weekEndingIso) {
    return null;
  }

  try {
    const linesSnapshot = await getDocs(
      collection(db, "venues", venueIdValue, "budget_weeks", weekEndingIso, "lines")
    );

    if (linesSnapshot.empty) {
      return 0;
    }

    let total = 0;

    linesSnapshot.forEach((lineDocument) => {
      total += extractBudgetLineAmount(lineDocument.data() || {});
    });

    return total;
  } catch (fetchError) {
    console.warn(
      "[WeeklyRevenueRollingLinesOnlyContent] budget read failed:",
      weekEndingIso,
      fetchError
    );

    return null;
  }
}

function getBudgetForWeekSync(weekEndingIso) {
  if (typeof props.resolveBudget === "function") {
    const value = props.resolveBudget(weekEndingIso);

    return value == null ? null : Number(value);
  }

  const value = props.budgetByWeekEnding?.[weekEndingIso];

  return value == null ? null : Number(value);
}

async function getBudgetForWeek(venueIdValue, weekEndingIso) {
  const suppliedBudget = getBudgetForWeekSync(weekEndingIso);

  if (suppliedBudget != null && Number.isFinite(suppliedBudget)) {
    return suppliedBudget;
  }

  return fetchBudgetForWeekFS(venueIdValue, weekEndingIso);
}

function extractTillDocTotal(data) {
  if (!data || typeof data !== "object") {
    return 0;
  }

  const directValue =
    data.counted_total ??
    data.countedTotal ??
    data.total_counted ??
    data.actual_total ??
    data.actualTotal ??
    data.total ??
    data.grand_total ??
    data.grandTotal ??
    data.value ??
    data.amount ??
    data?.totals?.counted_total ??
    data?.totals?.total ??
    null;

  if (directValue != null) {
    return toNumber(directValue);
  }

  const ignoredFields = new Set([
    "name",
    "label",
    "type",
    "id",
    "uid",
    "email",
    "updated_at",
    "updatedAt",
    "created_at",
    "createdAt",
    "venue_id",
    "venueId",
    "trading_date",
    "tradingDate",
    "week_ending_date",
    "weekEndingDate",
  ]);

  let total = 0;

  for (const [key, value] of Object.entries(data)) {
    if (ignoredFields.has(key)) {
      continue;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      total += value;
    }
  }

  return total;
}

async function fetchActualForDayFS(venueIdValue, tradingDateIso) {
  const dayReference = doc(db, "venues", venueIdValue, "tills_days", tradingDateIso);

  try {
    const daySnapshot = await getDoc(dayReference);

    if (daySnapshot.exists()) {
      const dayData = daySnapshot.data() || {};

      const rolledTotal =
        dayData.counted_total ??
        dayData.countedTotal ??
        dayData.total_counted ??
        dayData.actual_total ??
        dayData.actualTotal ??
        dayData.total ??
        dayData?.totals?.counted_total ??
        null;

      if (rolledTotal != null) {
        return toNumber(rolledTotal);
      }
    }

    const tillsSnapshot = await getDocs(
      collection(db, "venues", venueIdValue, "tills_days", tradingDateIso, "tills")
    );

    if (tillsSnapshot.empty) {
      return 0;
    }

    let total = 0;

    tillsSnapshot.forEach((tillSnapshot) => {
      total += extractTillDocTotal(tillSnapshot.data() || {});
    });

    return total;
  } catch (fetchError) {
    console.warn(
      "[WeeklyRevenueRollingLinesOnlyContent] tills_days read failed:",
      tradingDateIso,
      fetchError
    );

    return 0;
  }
}

async function fetchActualForWeekFS(venueIdValue, weekEndingIso) {
  const weekStart = weekStartFromWeekEnding(weekEndingIso);

  if (!weekStart) {
    return null;
  }

  const dayTotals = await Promise.all(
    Array.from(
      {
        length: 7,
      },

      (_, index) => {
        const day = addDays(weekStart, index);

        const iso = toISODateLocal(day);

        return fetchActualForDayFS(venueIdValue, iso);
      }
    )
  );

  return dayTotals.reduce((sum, value) => sum + toNumber(value), 0);
}

const actualStroke = computed(() => "#00E5FF");

const forecastStroke = computed(() => "#76FF7A");

const budgetStroke = computed(() => "#FFEA00");

const _sharedAuditGreen = auditColours.AUDIT_BG_GREEN;

void actualStroke;
void forecastStroke;
void budgetStroke;
void _sharedAuditGreen;

function getCurrentWeekEndingIso() {
  return getWeekEndingFromAnyDate(toISODateLocal(new Date()));
}

async function load() {
  error.value = "";

  weeks.value = [];

  const activeVenueId = venueId.value;

  const currentWeekEnding = getCurrentWeekEndingIso();

  if (!activeVenueId || !currentWeekEnding) {
    return;
  }

  loading.value = true;

  try {
    const weekEndings = buildRollingWeekEndings(currentWeekEnding);

    const rows = await mapLimit(
      weekEndings,

      Math.max(1, Number(props.edgeConcurrency) || 12),

      async (weekEndingIso) => {
        const [actual, forecast, budget] = await Promise.all([
          fetchActualForWeekFS(activeVenueId, weekEndingIso),

          getForecastForWeek(activeVenueId, weekEndingIso),

          getBudgetForWeek(activeVenueId, weekEndingIso),
        ]);

        return {
          key: weekEndingIso,

          weekEnding: weekEndingIso,

          labelShort: formatWeekLabelShort(weekEndingIso),

          actual: actual == null ? null : Number(actual),

          forecast: forecast == null ? null : Number(forecast),

          budget: budget == null ? null : Number(budget),
        };
      }
    );

    weeks.value = rows;
  } catch (loadError) {
    error.value = loadError?.message || String(loadError);
  } finally {
    loading.value = false;
  }
}

watch(venueId, load, {
  immediate: true,
});

watch(
  () => [props.forecastByWeekEnding, props.budgetByWeekEnding],

  () => {
    weeks.value = (weeks.value || []).map((week) => ({
      ...week,

      forecast: getForecastForWeekSync(week.weekEnding) ?? week.forecast,

      budget: getBudgetForWeekSync(week.weekEnding) ?? week.budget,
    }));
  },

  {
    deep: true,
  }
);

const svgW = 900;

const svgH = 320;

const padL = 88;

const padR = 18;

const padT = 12;

const padB = 48;

const plotW = svgW - padL - padR;

const plotH = svgH - padT - padB;

function xFor(index) {
  const numberOfIntervals = Math.max(1, weeks.value.length - 1);

  const position = index / numberOfIntervals;

  return padL + position * plotW;
}

const actualTrend = computed(() => {
  const observations = weeks.value
    .map((week, index) => ({
      index,

      value: week.actual == null ? null : Number(week.actual),
    }))
    .filter((observation) => Number.isFinite(observation.value));

  if (observations.length < 2) {
    return null;
  }

  const meanX =
    observations.reduce((sum, observation) => sum + observation.index, 0) /
    observations.length;

  const meanY =
    observations.reduce((sum, observation) => sum + observation.value, 0) /
    observations.length;

  const numerator = observations.reduce(
    (sum, observation) => sum + (observation.index - meanX) * (observation.value - meanY),
    0
  );

  const denominator = observations.reduce(
    (sum, observation) => sum + (observation.index - meanX) ** 2,
    0
  );

  if (denominator === 0) {
    return null;
  }

  const slope = numerator / denominator;

  const intercept = meanY - slope * meanX;

  const startIndex = observations[0].index;

  const endIndex = observations[observations.length - 1].index;

  return {
    startIndex,

    endIndex,

    startValue: Math.max(0, intercept + slope * startIndex),

    endValue: Math.max(0, intercept + slope * endIndex),
  };
});

const allValues = computed(() => {
  const values = [];

  for (const week of weeks.value) {
    if (week.actual != null) {
      values.push(Number(week.actual));
    }

    if (week.forecast != null) {
      values.push(Number(week.forecast));
    }

    if (week.budget != null) {
      values.push(Number(week.budget));
    }
  }

  if (actualTrend.value) {
    values.push(
      actualTrend.value.startValue,

      actualTrend.value.endValue
    );
  }

  if (!values.length) {
    values.push(0);
  }

  return values.filter((value) => Number.isFinite(value));
});

const yDomain = computed(() => {
  const values = allValues.value;

  let minimum = Math.min(...values);

  let maximum = Math.max(...values);

  if (minimum === maximum) {
    maximum = minimum + 1;
  }

  const span = maximum - minimum;

  minimum = Math.max(0, minimum - span * 0.1);

  maximum += span * 0.1;

  return {
    min: minimum,
    max: maximum,
  };
});

function yFor(value) {
  const number = Number(value);

  const { min, max } = yDomain.value;

  const position = (number - min) / (max - min);

  return padT + (1 - position) * plotH;
}

const actualTrendLine = computed(() => {
  if (!actualTrend.value) {
    return null;
  }

  return {
    x1: xFor(actualTrend.value.startIndex),

    y1: yFor(actualTrend.value.startValue),

    x2: xFor(actualTrend.value.endIndex),

    y2: yFor(actualTrend.value.endValue),
  };
});

const yTicks = computed(() => {
  const { min, max } = yDomain.value;

  const numberOfIntervals = 4;

  const output = [];

  for (let index = 0; index <= numberOfIntervals; index += 1) {
    const position = index / numberOfIntervals;

    const value = min + (max - min) * position;

    output.push({
      v: value,
      y: yFor(value),
    });
  }

  return output;
});

function pointsForKey(key) {
  const points = [];

  weeks.value.forEach((week, index) => {
    const value = week[key];

    if (value == null) {
      return;
    }

    points.push(`${xFor(index)},${yFor(value)}`);
  });

  return points.length ? points.join(" ") : "";
}

const pointsActual = computed(() => pointsForKey("actual"));

const pointsForecast = computed(() => pointsForKey("forecast"));

const pointsBudget = computed(() => pointsForKey("budget"));
</script>

<style scoped>
.chart-wrap {
  padding: 0;
  margin: 0;
}

.chart-scroll {
  overflow-x: auto;
}

.chart-svg {
  min-width: 900px;
  display: block;
}

.plot-border {
  fill: transparent;
  stroke: rgba(0, 0, 0, 0.15);
}

.grid-line {
  stroke: rgba(0, 0, 0, 0.08);
}

.tick-line {
  stroke: rgba(0, 0, 0, 0.25);
}

.axis-text {
  fill: rgba(0, 0, 0, 0.7);
}

svg polyline {
  fill: none !important;
}

.line-actual,
.line-forecast,
.line-budget,
.line-trend {
  fill: none !important;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.line-actual {
  stroke: #2196f3;
  stroke-width: 3.25;
}

.pt-actual {
  fill: #2196f3;
}

.line-forecast {
  stroke: #f44336;
  stroke-width: 2.75;
  stroke-dasharray: 4 3;
}

.pt-forecast {
  fill: #f44336;
}

.line-budget {
  stroke: #ffc107;
  stroke-width: 2.75;
}

.pt-budget {
  fill: #ffc107;
}

.line-trend {
  stroke: #e311f2;
  stroke-width: 2.5;
  stroke-dasharray: 10 6;
}

.legend {
  margin-top: 10px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  margin-right: 14px;
  font-size: 0.95rem;
}

.legend-swatch {
  display: inline-block;
  width: 16px;
  height: 4px;
  margin-right: 8px;
  border-radius: 2px;
}

.swatch-actual {
  background: #2196f3;
}

.swatch-forecast {
  background: #f44336;
}

.swatch-budget {
  background: #ffc107;
}

.swatch-trend {
  height: 0;
  border-top: 3px dashed #0d47a1;
  border-radius: 0;
  background: transparent;
}

@media (max-width: 600px) {
  .chart-scroll {
    margin-right: -2px;
    margin-left: -2px;
    -webkit-overflow-scrolling: touch;
  }

  .chart-svg {
    min-width: 760px;
  }

  .axis-text {
    font-size: 10px;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
  }

  .legend-item {
    margin-right: 0;
    font-size: 0.85rem;
  }
}
</style>
